// src/pages/finance-portal/inventory/LotsSerials.js
//
// Lots + Serials with tab switcher.
// Lot recall action, serial history drill-in.

import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert, Autocomplete, Box, Button, Chip, Dialog, DialogActions, DialogContent,
    DialogTitle, FormControl, IconButton, InputAdornment, InputLabel, MenuItem,
    Paper, Select, Skeleton, Stack, Tab, Table, TableBody, TableCell, TableHead,
    TablePagination, TableRow, Tabs, TextField, Tooltip, Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import HistoryIcon from '@mui/icons-material/History';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import axiosInstance from '../../../utils/axiosInstance';
import {
    FONT_FAMILY, INV_ACCENT, INV_ACCENT_DARK, formatCurrency, formatDate, formatDateTime,
} from './components/inventoryFormatters';

const LOT_STATUS_COLORS = {
    AVAILABLE: { bg: '#dcfce7', color: '#166534' },
    QUARANTINED: { bg: '#fef3c7', color: '#92400e' },
    EXPIRED: { bg: '#fee2e2', color: '#991b1b' },
    RECALLED: { bg: '#fecaca', color: '#7f1d1d' },
    CONSUMED: { bg: '#e5e7eb', color: '#374151' },
};
const SERIAL_STATUS_COLORS = {
    AVAILABLE: { bg: '#dcfce7', color: '#166534' },
    RESERVED: { bg: '#fef3c7', color: '#92400e' },
    SHIPPED: { bg: '#dbeafe', color: '#1e40af' },
    RETURNED: { bg: '#fed7aa', color: '#9a3412' },
    QUARANTINED: { bg: '#fef3c7', color: '#92400e' },
    SCRAPPED: { bg: '#fecaca', color: '#7f1d1d' },
};

export default function LotsSerials() {
    const [tab, setTab] = useState(0);
    const [error, setError] = useState(null);

    // Lots
    const [lots, setLots] = useState([]);
    const [lotsLoading, setLotsLoading] = useState(true);
    const [lotStatus, setLotStatus] = useState('');
    const [recallDialog, setRecallDialog] = useState({ open: false, lot: null, reason: '' });

    // Serials
    const [serials, setSerials] = useState([]);
    const [serialsTotal, setSerialsTotal] = useState(0);
    const [serialsPage, setSerialsPage] = useState(0);
    const [serialsPerPage, setSerialsPerPage] = useState(25);
    const [serialSearch, setSerialSearch] = useState('');
    const [serialStatus, setSerialStatus] = useState('');
    const [serialsLoading, setSerialsLoading] = useState(true);

    // History
    const [historyDialog, setHistoryDialog] = useState({ open: false, serial: null, history: [] });

    const fetchLots = useCallback(async () => {
        setLotsLoading(true);
        try {
            const params = {};
            if (lotStatus) params.status = lotStatus;
            const res = await axiosInstance.get('/api/finance/inventory/lots', { params });
            if (res.data?.success) setLots(res.data.data || []);
        } catch (e) {
            setError(e.response?.data?.message || 'Failed to load lots');
        } finally {
            setLotsLoading(false);
        }
    }, [lotStatus]);

    const fetchSerials = useCallback(async () => {
        setSerialsLoading(true);
        try {
            const params = { page: serialsPage + 1, per_page: serialsPerPage };
            if (serialSearch) params.search = serialSearch;
            if (serialStatus) params.status = serialStatus;
            const res = await axiosInstance.get('/api/finance/inventory/serials', { params });
            if (res.data?.success) {
                setSerials(res.data.data || []);
                setSerialsTotal(res.data.total || 0);
            }
        } catch (e) {
            setError(e.response?.data?.message || 'Failed to load serials');
        } finally {
            setSerialsLoading(false);
        }
    }, [serialsPage, serialsPerPage, serialSearch, serialStatus]);

    useEffect(() => { if (tab === 0) fetchLots(); }, [tab, fetchLots]);
    useEffect(() => { if (tab === 1) fetchSerials(); }, [tab, fetchSerials]);

    const handleRecall = async () => {
        if (!recallDialog.lot) return;
        try {
            await axiosInstance.post(`/api/finance/inventory/lots/${recallDialog.lot.id}/recall`,
                { reason: recallDialog.reason });
            setRecallDialog({ open: false, lot: null, reason: '' });
            fetchLots();
        } catch (e) {
            alert(e.response?.data?.message || 'Recall failed');
        }
    };

    const openHistory = async (serial) => {
        try {
            const res = await axiosInstance.get(`/api/finance/inventory/serials/${serial.id}/history`);
            if (res.data?.success) {
                setHistoryDialog({ open: true, serial: res.data.data.serial, history: res.data.data.history });
            }
        } catch (e) {
            alert(e.response?.data?.message || 'Failed to load history');
        }
    };

    const chipStyle = (s, map) => {
        const c = map[s] || { bg: '#e5e7eb', color: '#374151' };
        return { backgroundColor: c.bg, color: c.color };
    };

    return (
        <Box sx={{ p: 3, fontFamily: FONT_FAMILY, bgcolor: '#f8fafc', minHeight: '100vh' }}>
            <Box sx={{ mb: 3 }}>
                <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: '1.65rem', fontWeight: 700, color: '#0f172a' }}>
                    Lots &amp; Serials
                </Typography>
                <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: '0.9rem', color: '#64748b', mt: 0.5 }}>
                    Batch traceability with recall management and per-unit chain of custody
                </Typography>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

            <Paper sx={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                <Tabs value={tab} onChange={(e, v) => setTab(v)}
                    sx={{ borderBottom: '1px solid #e2e8f0',
                          '& .MuiTab-root': { fontFamily: FONT_FAMILY, textTransform: 'none', fontWeight: 600 } }}>
                    <Tab label={`Lots (${lots.length})`} />
                    <Tab label={`Serials (${serialsTotal})`} />
                </Tabs>

                {tab === 0 && (
                    <Box sx={{ p: 2 }}>
                        <FormControl size="small" sx={{ minWidth: 200, mb: 2 }}>
                            <InputLabel>Status</InputLabel>
                            <Select value={lotStatus} label="Status"
                                onChange={(e) => setLotStatus(e.target.value)}>
                                <MenuItem value="">All</MenuItem>
                                {Object.keys(LOT_STATUS_COLORS).map((s) => (
                                    <MenuItem key={s} value={s}>{s}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }}>Lot #</TableCell>
                                    <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }}>Product</TableCell>
                                    <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }}>Manufactured</TableCell>
                                    <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }}>Expires</TableCell>
                                    <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }} align="right">Qty Remaining</TableCell>
                                    <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }}>Vendor</TableCell>
                                    <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }}>Status</TableCell>
                                    <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }} align="right">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {lotsLoading ? Array.from({ length: 4 }).map((_, i) => (
                                    <TableRow key={i}><TableCell colSpan={8}><Skeleton height={28} /></TableCell></TableRow>
                                )) : lots.length === 0 ? (
                                    <TableRow><TableCell colSpan={8} sx={{ py: 6, textAlign: 'center', color: '#64748b' }}>
                                        No lots on record
                                    </TableCell></TableRow>
                                ) : lots.map((l) => (
                                    <TableRow key={l.id}>
                                        <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }}>{l.lot_number}</TableCell>
                                        <TableCell sx={{ fontFamily: FONT_FAMILY, fontSize: '0.85rem' }}>
                                            {l.product_sku} <span style={{ color: '#64748b' }}>{l.product_name}</span>
                                        </TableCell>
                                        <TableCell sx={{ fontFamily: FONT_FAMILY, fontSize: '0.85rem' }}>{formatDate(l.manufactured_date)}</TableCell>
                                        <TableCell sx={{
                                            fontFamily: FONT_FAMILY, fontSize: '0.85rem',
                                            color: l.expiration_date && new Date(l.expiration_date) < new Date() ? '#dc2626' : '#0f172a',
                                            fontWeight: l.expiration_date && new Date(l.expiration_date) < new Date() ? 600 : 400,
                                        }}>
                                            {formatDate(l.expiration_date)}
                                        </TableCell>
                                        <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }} align="right">
                                            {l.quantity_remaining}
                                        </TableCell>
                                        <TableCell sx={{ fontFamily: FONT_FAMILY, fontSize: '0.85rem' }}>{l.vendor_name || '—'}</TableCell>
                                        <TableCell>
                                            <Chip size="small" label={l.status}
                                                style={chipStyle(l.status, LOT_STATUS_COLORS)}
                                                sx={{ fontFamily: FONT_FAMILY, fontWeight: 600, fontSize: '0.7rem' }} />
                                        </TableCell>
                                        <TableCell align="right">
                                            {l.status === 'AVAILABLE' && (
                                                <Tooltip title="Recall this lot">
                                                    <IconButton size="small" color="error"
                                                        onClick={() => setRecallDialog({ open: true, lot: l, reason: '' })}>
                                                        <WarningAmberIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Box>
                )}

                {tab === 1 && (
                    <Box sx={{ p: 2 }}>
                        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                            <TextField size="small" placeholder="Search serial number / barcode…"
                                onChange={(e) => setSerialSearch(e.target.value)}
                                sx={{ minWidth: 300 }}
                                InputProps={{ startAdornment: (
                                    <InputAdornment position="start"><SearchIcon sx={{ color: '#94a3b8' }} /></InputAdornment>
                                )}} />
                            <FormControl size="small" sx={{ minWidth: 180 }}>
                                <InputLabel>Status</InputLabel>
                                <Select value={serialStatus} label="Status"
                                    onChange={(e) => { setSerialStatus(e.target.value); setSerialsPage(0); }}>
                                    <MenuItem value="">All</MenuItem>
                                    {Object.keys(SERIAL_STATUS_COLORS).map((s) => (
                                        <MenuItem key={s} value={s}>{s}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Stack>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }}>Serial</TableCell>
                                    <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }}>Product</TableCell>
                                    <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }}>Lot</TableCell>
                                    <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }}>Location</TableCell>
                                    <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }}>Received</TableCell>
                                    <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }} align="right">Cost</TableCell>
                                    <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }}>Status</TableCell>
                                    <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }} align="right">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {serialsLoading ? Array.from({ length: 4 }).map((_, i) => (
                                    <TableRow key={i}><TableCell colSpan={8}><Skeleton height={28} /></TableCell></TableRow>
                                )) : serials.length === 0 ? (
                                    <TableRow><TableCell colSpan={8} sx={{ py: 6, textAlign: 'center', color: '#64748b' }}>
                                        No serials match
                                    </TableCell></TableRow>
                                ) : serials.map((s) => (
                                    <TableRow key={s.id}>
                                        <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }}>{s.serial_number}</TableCell>
                                        <TableCell sx={{ fontFamily: FONT_FAMILY, fontSize: '0.85rem' }}>{s.product_sku}</TableCell>
                                        <TableCell sx={{ fontFamily: FONT_FAMILY, fontSize: '0.85rem' }}>{s.lot_number || '—'}</TableCell>
                                        <TableCell sx={{ fontFamily: FONT_FAMILY, fontSize: '0.85rem' }}>{s.location_code || '—'}</TableCell>
                                        <TableCell sx={{ fontFamily: FONT_FAMILY, fontSize: '0.85rem' }}>{formatDateTime(s.received_at)}</TableCell>
                                        <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }} align="right">
                                            {formatCurrency(s.unit_cost)}
                                        </TableCell>
                                        <TableCell>
                                            <Chip size="small" label={s.status}
                                                style={chipStyle(s.status, SERIAL_STATUS_COLORS)}
                                                sx={{ fontFamily: FONT_FAMILY, fontWeight: 600, fontSize: '0.7rem' }} />
                                        </TableCell>
                                        <TableCell align="right">
                                            <Tooltip title="View history">
                                                <IconButton size="small" onClick={() => openHistory(s)}>
                                                    <HistoryIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                        <TablePagination component="div" count={serialsTotal} page={serialsPage}
                            onPageChange={(e, p) => setSerialsPage(p)}
                            rowsPerPage={serialsPerPage}
                            onRowsPerPageChange={(e) => { setSerialsPerPage(parseInt(e.target.value, 10)); setSerialsPage(0); }}
                            sx={{ '.MuiTablePagination-toolbar': { fontFamily: FONT_FAMILY } }} />
                    </Box>
                )}
            </Paper>

            {/* Recall */}
            <Dialog open={recallDialog.open} onClose={() => setRecallDialog({ open: false, lot: null, reason: '' })} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontFamily: FONT_FAMILY }}>Recall Lot</DialogTitle>
                <DialogContent dividers>
                    <Typography sx={{ fontFamily: FONT_FAMILY, mb: 2 }}>
                        Recalling lot <b>{recallDialog.lot?.lot_number}</b> — all AVAILABLE serials from this lot will be marked QUARANTINED.
                    </Typography>
                    <TextField fullWidth multiline rows={3} label="Recall reason (required)"
                        value={recallDialog.reason}
                        onChange={(e) => setRecallDialog((d) => ({ ...d, reason: e.target.value }))} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRecallDialog({ open: false, lot: null, reason: '' })}>Cancel</Button>
                    <Button variant="contained" color="error" disabled={!recallDialog.reason.trim()} onClick={handleRecall}>
                        Recall
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Serial history */}
            <Dialog open={historyDialog.open} onClose={() => setHistoryDialog({ open: false, serial: null, history: [] })} maxWidth="md" fullWidth>
                {historyDialog.serial && (
                    <>
                        <DialogTitle sx={{ fontFamily: FONT_FAMILY }}>
                            {historyDialog.serial.serial_number} — Chain of Custody
                        </DialogTitle>
                        <DialogContent dividers>
                            {historyDialog.history.length === 0 ? (
                                <Typography sx={{ fontFamily: FONT_FAMILY, color: '#64748b', textAlign: 'center', py: 4 }}>
                                    No history events recorded.
                                </Typography>
                            ) : (
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }}>When</TableCell>
                                            <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }}>From</TableCell>
                                            <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }}>To</TableCell>
                                            <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }}>Notes</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {historyDialog.history.map((h) => (
                                            <TableRow key={h.id}>
                                                <TableCell sx={{ fontFamily: FONT_FAMILY, fontSize: '0.82rem' }}>{formatDateTime(h.created_at)}</TableCell>
                                                <TableCell sx={{ fontFamily: FONT_FAMILY }}>{h.from_status || '—'}</TableCell>
                                                <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }}>{h.to_status}</TableCell>
                                                <TableCell sx={{ fontFamily: FONT_FAMILY, fontSize: '0.82rem', color: '#64748b' }}>{h.notes || '—'}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            )}
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setHistoryDialog({ open: false, serial: null, history: [] })}>Close</Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>
        </Box>
    );
}
